import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { jsPDF } from "jspdf";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Shield,
  Building2,
  Users,
  Clock,
  FileText,
  Plus,
  Trash2,
  Check,
  X,
  Globe,
  Download,
  MapPin,
  ChevronDown,
  ChevronRight,
  UserPlus,
  Calendar,
  ChevronLeft,
  Lock,
  Mail,
  Briefcase,
  Pencil,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_COLORS = [
  "#2563EB", "#7C3AED", "#DB2777", "#DC2626",
  "#EA580C", "#CA8A04", "#16A34A", "#0891B2",
];

const MONTHS = [
  "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember"
];

export const AdminDashboard = () => {
  const [stats, setStats] = useState({
    total_entries: 0,
    signed_entries: 0,
    total_projects: 0,
    global_projects: 0,
    unique_employees: 0,
  });
  const [entries, setEntries] = useState([]);
  const [globalProjects, setGlobalProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [monthlyStats, setMonthlyStats] = useState({ employees: [] });
  const [selectedCompany, setSelectedCompany] = useState("all");
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [newEinrichtung, setNewEinrichtung] = useState({ name: "", color: "#7C3AED" });
  const [newStation, setNewStation] = useState({ name: "", color: "#2563EB", parent_id: "" });
  const [newEmployee, setNewEmployee] = useState({ name: "", code: "", company: "" });
  const [newCompany, setNewCompany] = useState({ code: "", name: "", email: "", street: "", zip_code: "", city: "", country: "Deutschland" });
  const [createdEmployee, setCreatedEmployee] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [editingCompany, setEditingCompany] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedEinrichtungen, setExpandedEinrichtungen] = useState({});

  useEffect(() => {
    fetchData();
  }, [selectedCompany, selectedMonth, selectedYear]);

  const fetchData = async () => {
    try {
      const [statsRes, entriesRes, projectsRes, employeesRes, companiesRes, monthlyRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/entries${selectedCompany !== "all" ? `?company_code=${selectedCompany}` : ""}`),
        axios.get(`${API}/projects/global`),
        axios.get(`${API}/employees`),
        axios.get(`${API}/companies`),
        axios.get(`${API}/stats/monthly/all?year=${selectedYear}&month=${selectedMonth}`),
      ]);
      setStats(statsRes.data);
      setEntries(entriesRes.data);
      setGlobalProjects(projectsRes.data);
      setEmployees(employeesRes.data);
      setCompanies(companiesRes.data);
      setMonthlyStats(monthlyRes.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Daten");
    }
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    if (!newCompany.code.trim() || !newCompany.name.trim() || !newCompany.email.trim()) {
      toast.error("Bitte alle Felder ausfüllen");
      return;
    }
    setIsLoading(true);
    try {
      await axios.post(`${API}/companies`, {
        ...newCompany,
        code: newCompany.code.toUpperCase(),
      });
      toast.success("Firma angelegt");
      setNewCompany({ code: "", name: "", email: "", street: "", zip_code: "", city: "", country: "Deutschland" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Anlegen");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateCompany = async (companyId, updateData) => {
    try {
      await axios.put(`${API}/companies/${companyId}`, updateData);
      toast.success("Firma aktualisiert");
      setEditingCompany(null);
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const handleDeleteCompany = async (id) => {
    try {
      await axios.delete(`${API}/companies/${id}`);
      toast.success("Firma gelöscht");
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const handleCreateEmployee = async (e) => {
    e.preventDefault();
    if (!newEmployee.name.trim() || !newEmployee.code.trim() || !newEmployee.company) {
      toast.error("Bitte alle Felder ausfüllen");
      return;
    }
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/employees`, newEmployee);
      const created = response.data;
      setCreatedEmployee(created);
      toast.success("Mitarbeiter angelegt!");
      setNewEmployee({ name: "", code: "", company: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Anlegen");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteEmployee = async (id) => {
    try {
      await axios.delete(`${API}/employees/${id}`);
      toast.success("Mitarbeiter deaktiviert");
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const handleCreateEinrichtung = async (e) => {
    e.preventDefault();
    if (!newEinrichtung.name.trim()) {
      toast.error("Bitte Namen eingeben");
      return;
    }
    setIsLoading(true);
    try {
      await axios.post(`${API}/projects`, {
        ...newEinrichtung,
        type: "einrichtung",
        employee_code: "ADMIN",
        is_global: true,
      });
      toast.success("Einrichtung erstellt");
      setNewEinrichtung({ name: "", color: "#7C3AED" });
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Erstellen");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateStation = async (e) => {
    e.preventDefault();
    if (!newStation.name.trim() || !newStation.parent_id) {
      toast.error("Bitte alle Felder ausfüllen");
      return;
    }
    setIsLoading(true);
    try {
      await axios.post(`${API}/projects`, {
        name: newStation.name,
        color: newStation.color,
        type: "station",
        parent_id: newStation.parent_id,
        employee_code: "ADMIN",
        is_global: true,
      });
      toast.success("Station/WB erstellt");
      setNewStation({ name: "", color: "#2563EB", parent_id: "" });
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Erstellen");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteProject = async (id) => {
    try {
      await axios.delete(`${API}/projects/${id}`);
      toast.success("Eintrag gelöscht");
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const handleUpdateProject = async (id, name, color) => {
    try {
      await axios.put(`${API}/projects/${id}`, { name, color });
      toast.success("Änderungen gespeichert");
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const handleDeleteEntry = async (id) => {
    try {
      await axios.delete(`${API}/time-entries/${id}`);
      toast.success("Eintrag gelöscht");
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const toggleEinrichtung = (id) => {
    setExpandedEinrichtungen(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const prevMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const nextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const calculateWorkTime = (start, end, breakMins) => {
    const [sh, sm] = start.split(":").map(Number);
    const [eh, em] = end.split(":").map(Number);
    let startMins = sh * 60 + sm;
    let endMins = eh * 60 + em;
    if (endMins < startMins) endMins += 24 * 60;
    const totalMins = endMins - startMins - breakMins;
    const hours = Math.floor(totalMins / 60);
    const mins = totalMins % 60;
    return `${hours}h ${mins}m`;
  };

  const generatePDF = (entry, empCode) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Find employee to get their company code
    const emp = employees.find(e => e.code === entry.employee_code);
    const employeeCompanyCode = emp?.company || entry.employee_code;
    
    // Find company name using the employee's company code
    const company = companies.find(c => c.code === employeeCompanyCode);
    
    const employeeName = entry.employee_name || emp?.name || entry.employee_code;
    const companyName = entry.company_name || company?.name || employeeCompanyCode;
    
    // Get company address
    const companyAddress = company ? 
      [company.street, `${company.zip_code || ''} ${company.city || ''}`.trim()].filter(Boolean).join(", ") : "";
    
    doc.setFillColor(37, 99, 235);
    doc.rect(0, 0, pageWidth, 40, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont("helvetica", "bold");
    doc.text("ZeitFlow", 20, 25);
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text("Arbeitszeitnachweis", 20, 33);
    
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Arbeitszeiterfassung", 20, 55);
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    
    const entryDate = format(new Date(entry.date), "EEEE, d. MMMM yyyy", { locale: de });
    const workTime = calculateWorkTime(entry.start_time, entry.end_time, entry.break_minutes);
    
    const details = [
      { label: "Mitarbeiter:", value: employeeName },
      { label: "Firma:", value: companyName },
    ];
    
    // Add address if available
    if (companyAddress) {
      details.push({ label: "Adresse:", value: companyAddress });
    }
    
    details.push(
      { label: "Datum:", value: entryDate },
      { label: "Arbeitsbeginn:", value: entry.start_time + " Uhr" },
      { label: "Arbeitsende:", value: entry.end_time + " Uhr" },
      { label: "Pause:", value: entry.break_minutes + " Minuten" },
      { label: "Netto-Arbeitszeit:", value: workTime },
      { label: "Einrichtung:", value: entry.einrichtung || "Keine Angabe" },
      { label: "Station/WB:", value: entry.station_wb || "Keine Angabe" },
    );
    
    let yPos = 70;
    details.forEach(({ label, value }) => {
      doc.setFont("helvetica", "bold");
      doc.text(label, 20, yPos);
      doc.setFont("helvetica", "normal");
      doc.text(value || "", 80, yPos);
      yPos += 10;
    });
    
    doc.setDrawColor(200, 200, 200);
    doc.line(20, yPos + 10, pageWidth - 20, yPos + 10);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("Unterschrift", 20, yPos + 25);
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text("Name des Unterzeichners:", 20, yPos + 40);
    doc.setFont("helvetica", "bold");
    doc.text(entry.signer_name || "Unbekannt", 80, yPos + 40);
    doc.setFont("helvetica", "normal");
    doc.text("Unterschrieben am:", 20, yPos + 50);
    if (entry.signed_at) {
      doc.text(format(new Date(entry.signed_at), "dd.MM.yyyy 'um' HH:mm 'Uhr'", { locale: de }), 80, yPos + 50);
    }
    if (entry.signature) {
      doc.addImage(entry.signature, 'PNG', 20, yPos + 60, 80, 40);
    }
    
    // Footer with legal notice
    doc.setDrawColor(200, 200, 200);
    doc.line(20, 260, pageWidth - 20, 260);
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text("Gemäß Arbeitszeitgesetz (ArbZG) darf die werktägliche Arbeitszeit von 10 Std. nicht überschritten werden.", 20, 268);
    
    doc.setDrawColor(200, 200, 200);
    doc.line(20, 272, pageWidth - 20, 272);
    doc.setFontSize(9);
    doc.text("Erstellt mit ZeitFlow - Arbeitszeiterfassung", 20, 280);
    
    const fileName = `Arbeitszeit_${entry.date}_${employeeName.replace(/\s+/g, '_')}.pdf`;
    doc.save(fileName);
    toast.success(`PDF heruntergeladen`);
  };

  const globalEinrichtungen = globalProjects.filter(p => p.type === "einrichtung" || !p.type);
  const globalStationen = globalProjects.filter(p => p.type === "station");
  const getStationenForEinrichtung = (einrichtungId) => globalStationen.filter(s => s.parent_id === einrichtungId);

  const entriesByEmployee = entries.reduce((groups, entry) => {
    const key = entry.employee_code || "Unbekannt";
    if (!groups[key]) groups[key] = [];
    groups[key].push(entry);
    return groups;
  }, {});

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Page Header */}
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center">
          <Shield className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-3xl font-bold text-slate-900 tracking-tight font-['Manrope']">Admin-Bereich</h2>
          <p className="text-slate-500">Mitarbeiter, Zeiterfassung & Stammdaten</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="zeitflow-card">
          <CardContent className="pt-6">
            <div className="stats-value text-blue-600">{employees.length}</div>
            <div className="stats-label">Mitarbeiter</div>
          </CardContent>
        </Card>
        <Card className="zeitflow-card">
          <CardContent className="pt-6">
            <div className="stats-value">{stats.total_entries}</div>
            <div className="stats-label">Zeiteinträge</div>
          </CardContent>
        </Card>
        <Card className="zeitflow-card">
          <CardContent className="pt-6">
            <div className="stats-value text-green-600">{stats.signed_entries}</div>
            <div className="stats-label">Unterschrieben</div>
          </CardContent>
        </Card>
        <Card className="zeitflow-card">
          <CardContent className="pt-6">
            <div className="stats-value text-purple-600">{globalEinrichtungen.length}</div>
            <div className="stats-label">Einrichtungen</div>
          </CardContent>
        </Card>
        <Card className="zeitflow-card">
          <CardContent className="pt-6">
            <div className="stats-value text-blue-600">{globalStationen.length}</div>
            <div className="stats-label">Stationen</div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="monthly" className="space-y-6">
        <TabsList className="flex-wrap">
          <TabsTrigger value="monthly" className="gap-2">
            <Calendar className="w-4 h-4" />
            Monatsübersicht
          </TabsTrigger>
          <TabsTrigger value="employees" className="gap-2">
            <Users className="w-4 h-4" />
            Mitarbeiter
          </TabsTrigger>
          <TabsTrigger value="companies" className="gap-2">
            <Briefcase className="w-4 h-4" />
            Firmen
          </TabsTrigger>
          <TabsTrigger value="entries" className="gap-2">
            <FileText className="w-4 h-4" />
            Zeiteinträge
          </TabsTrigger>
          <TabsTrigger value="stammdaten" className="gap-2">
            <Building2 className="w-4 h-4" />
            Einrichtungen
          </TabsTrigger>
        </TabsList>

        {/* Monthly Overview Tab */}
        <TabsContent value="monthly" className="space-y-4">
          <Card className="zeitflow-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  Monatsstunden
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={prevMonth}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="font-semibold min-w-[150px] text-center">
                    {MONTHS[selectedMonth - 1]} {selectedYear}
                  </span>
                  <Button variant="outline" size="sm" onClick={nextMonth}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {monthlyStats.employees?.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Clock className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>Keine Einträge in diesem Monat</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-5 gap-4 p-3 bg-slate-100 rounded-lg font-semibold text-sm text-slate-600">
                    <div>Mitarbeiter</div>
                    <div className="text-center">Stunden</div>
                    <div className="text-center">Einträge</div>
                    <div className="text-center">Unterschrieben</div>
                    <div className="text-center">Tage</div>
                  </div>
                  {monthlyStats.employees?.map((emp, idx) => (
                    <div key={idx} className="grid grid-cols-5 gap-4 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 items-center">
                      <div className="font-medium text-slate-900">{emp.employee_name}</div>
                      <div className="text-center text-blue-600 font-semibold">{emp.total_hours}h</div>
                      <div className="text-center">{emp.total_entries}</div>
                      <div className="text-center">
                        {emp.signed_entries === emp.total_entries ? (
                          <span className="text-green-600">{emp.signed_entries} ✓</span>
                        ) : (
                          <span className="text-amber-600">{emp.signed_entries}/{emp.total_entries}</span>
                        )}
                      </div>
                      <div className="text-center">{emp.days_worked}</div>
                    </div>
                  ))}
                  {/* Total Row */}
                  <div className="grid grid-cols-5 gap-4 p-3 bg-blue-50 rounded-lg font-semibold border-2 border-blue-200">
                    <div className="text-blue-900">Gesamt</div>
                    <div className="text-center text-blue-600">
                      {monthlyStats.employees?.reduce((sum, e) => sum + e.total_hours, 0).toFixed(1)}h
                    </div>
                    <div className="text-center">{monthlyStats.employees?.reduce((sum, e) => sum + e.total_entries, 0)}</div>
                    <div className="text-center">{monthlyStats.employees?.reduce((sum, e) => sum + e.signed_entries, 0)}</div>
                    <div className="text-center">-</div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Employees Tab */}
        <TabsContent value="employees" className="space-y-4">
          {/* Success Modal for created employee */}
          {createdEmployee && (
            <Card className="zeitflow-card border-2 border-green-500 bg-green-50">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-green-700">
                      <Check className="w-5 h-5" />
                      <span className="font-semibold">Mitarbeiter erfolgreich angelegt!</span>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-green-200 space-y-2">
                      <div className="text-sm text-slate-600">
                        <span className="font-medium">Name:</span> {createdEmployee.name}
                      </div>
                      <div className="text-sm text-slate-600">
                        <span className="font-medium">Mitarbeiter-Code:</span>{" "}
                        <span className="font-mono bg-slate-100 px-2 py-1 rounded">{createdEmployee.code}</span>
                      </div>
                      <div className="text-sm text-slate-600">
                        <span className="font-medium">Passwort:</span>{" "}
                        <span className="font-mono bg-yellow-100 px-2 py-1 rounded text-lg font-bold tracking-widest">
                          {createdEmployee.password}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-amber-700 flex items-center gap-1">
                      <Lock className="w-3 h-3" />
                      Bitte notieren Sie das Passwort - es wird nur einmal angezeigt!
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCreatedEmployee(null)}
                    className="text-slate-500"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="zeitflow-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <UserPlus className="w-5 h-5 text-blue-600" />
                  Neuer Mitarbeiter
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreateEmployee} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Name</Label>
                    <Input
                      value={newEmployee.name}
                      onChange={(e) => setNewEmployee({ ...newEmployee, name: e.target.value })}
                      placeholder="Vor- und Nachname"
                      data-testid="new-employee-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Mitarbeiter-Code</Label>
                    <Input
                      value={newEmployee.code}
                      onChange={(e) => setNewEmployee({ ...newEmployee, code: e.target.value.toUpperCase() })}
                      placeholder="z.B. MA001"
                      className="font-mono"
                      data-testid="new-employee-code"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Firma</Label>
                    <Select value={newEmployee.company} onValueChange={(v) => setNewEmployee({ ...newEmployee, company: v })}>
                      <SelectTrigger data-testid="new-employee-company">
                        <SelectValue placeholder="Firma auswählen..." />
                      </SelectTrigger>
                      <SelectContent>
                        {companies.map((c) => (
                          <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={isLoading}>
                    <Plus className="w-4 h-4 mr-2" />
                    Mitarbeiter anlegen
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card className="lg:col-span-2 zeitflow-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Users className="w-5 h-5 text-blue-600" />
                  Alle Mitarbeiter ({employees.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {employees.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Users className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>Noch keine Mitarbeiter angelegt</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {employees.map((emp) => {
                      const company = companies.find(c => c.code === emp.company);
                      return (
                        <div key={emp.id} className="flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:bg-slate-50">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold">
                              {emp.name.charAt(0)}
                            </div>
                            <div>
                              <div className="font-medium text-slate-900">{emp.name}</div>
                              <div className="text-sm text-slate-500 flex items-center gap-2 flex-wrap">
                                <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded text-xs">{emp.code}</span>
                                <span>•</span>
                                {emp.password && (
                                  <span className="font-mono bg-yellow-50 px-1.5 py-0.5 rounded text-xs text-amber-700 flex items-center gap-1">
                                    <Lock className="w-3 h-3" />
                                    {emp.password}
                                  </span>
                                )}
                                {!emp.password && (
                                  <span className="text-xs text-red-500">Kein Passwort</span>
                                )}
                                <span>•</span>
                                <span className="text-xs">{company?.name || emp.company}</span>
                              </div>
                            </div>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteEmployee(emp.id)} className="text-red-600 hover:bg-red-50">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Companies Tab */}
        <TabsContent value="companies" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="zeitflow-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Briefcase className="w-5 h-5 text-blue-600" />
                  Neue Firma
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreateCompany} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Firmen-Code</Label>
                    <Input
                      value={newCompany.code}
                      onChange={(e) => setNewCompany({ ...newCompany, code: e.target.value.toUpperCase() })}
                      placeholder="z.B. FIRMAXY"
                      className="font-mono"
                      data-testid="new-company-code"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Firmenname</Label>
                    <Input
                      value={newCompany.name}
                      onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })}
                      placeholder="z.B. Firma XY GmbH"
                      data-testid="new-company-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>E-Mail für Arbeitszeitnachweise</Label>
                    <Input
                      type="email"
                      value={newCompany.email}
                      onChange={(e) => setNewCompany({ ...newCompany, email: e.target.value })}
                      placeholder="zeiterfassung@firma.de"
                      data-testid="new-company-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Straße & Hausnummer</Label>
                    <Input
                      value={newCompany.street}
                      onChange={(e) => setNewCompany({ ...newCompany, street: e.target.value })}
                      placeholder="z.B. Musterstraße 123"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-2">
                      <Label>PLZ</Label>
                      <Input
                        value={newCompany.zip_code}
                        onChange={(e) => setNewCompany({ ...newCompany, zip_code: e.target.value })}
                        placeholder="12345"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Stadt</Label>
                      <Input
                        value={newCompany.city}
                        onChange={(e) => setNewCompany({ ...newCompany, city: e.target.value })}
                        placeholder="Musterstadt"
                      />
                    </div>
                  </div>
                  <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={isLoading}>
                    <Plus className="w-4 h-4 mr-2" />
                    Firma anlegen
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card className="lg:col-span-2 zeitflow-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Briefcase className="w-5 h-5 text-blue-600" />
                  Alle Firmen ({companies.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {companies.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Briefcase className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>Noch keine Firmen angelegt</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {companies.map((comp) => (
                      <div key={comp.id} className="p-4 rounded-lg border border-slate-200 hover:bg-slate-50">
                        {editingCompany?.id === comp.id ? (
                          <div className="space-y-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div className="space-y-1">
                                <Label className="text-xs text-slate-500">Firmenname</Label>
                                <Input
                                  value={editingCompany.name}
                                  onChange={(e) => setEditingCompany({ ...editingCompany, name: e.target.value })}
                                  className="h-9"
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs text-slate-500">E-Mail</Label>
                                <Input
                                  type="email"
                                  value={editingCompany.email}
                                  onChange={(e) => setEditingCompany({ ...editingCompany, email: e.target.value })}
                                  className="h-9"
                                />
                              </div>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-slate-500">Straße & Hausnummer</Label>
                              <Input
                                value={editingCompany.street || ""}
                                onChange={(e) => setEditingCompany({ ...editingCompany, street: e.target.value })}
                                className="h-9"
                              />
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                              <div className="space-y-1">
                                <Label className="text-xs text-slate-500">PLZ</Label>
                                <Input
                                  value={editingCompany.zip_code || ""}
                                  onChange={(e) => setEditingCompany({ ...editingCompany, zip_code: e.target.value })}
                                  className="h-9"
                                />
                              </div>
                              <div className="space-y-1 col-span-2">
                                <Label className="text-xs text-slate-500">Stadt</Label>
                                <Input
                                  value={editingCompany.city || ""}
                                  onChange={(e) => setEditingCompany({ ...editingCompany, city: e.target.value })}
                                  className="h-9"
                                />
                              </div>
                            </div>
                            <div className="flex gap-2 pt-2">
                              <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleUpdateCompany(editingCompany.id, editingCompany)}>
                                <Check className="w-4 h-4 mr-1" /> Speichern
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setEditingCompany(null)}>
                                <X className="w-4 h-4 mr-1" /> Abbrechen
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <div className="flex items-start justify-between">
                              <div>
                                <div className="font-medium text-slate-900">{comp.name}</div>
                                <div className="text-sm text-slate-500 font-mono">{comp.code}</div>
                              </div>
                              <div className="flex gap-1">
                                <Button variant="ghost" size="sm" onClick={() => setEditingCompany({...comp})} className="text-slate-600 hover:bg-slate-100">
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleDeleteCompany(comp.id)} className="text-red-600 hover:bg-red-50">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                            <div className="text-sm text-slate-600 space-y-1">
                              <div className="flex items-center gap-2">
                                <Mail className="w-4 h-4 text-slate-400" />
                                {comp.email}
                              </div>
                              {(comp.street || comp.city) && (
                                <div className="flex items-start gap-2">
                                  <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                                  <div>
                                    {comp.street && <div>{comp.street}</div>}
                                    {(comp.zip_code || comp.city) && (
                                      <div>{comp.zip_code} {comp.city}</div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Entries Tab */}
        <TabsContent value="entries" className="space-y-4">
          <div className="flex items-center gap-4">
            <Label>Filter nach Firma:</Label>
            <Select value={selectedCompany} onValueChange={setSelectedCompany}>
              <SelectTrigger className="w-64">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Firmen</SelectItem>
                {companies.map((c) => (
                  <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Card className="zeitflow-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                Alle Zeiteinträge ({entries.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {Object.keys(entriesByEmployee).length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Clock className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>Keine Einträge vorhanden</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {Object.entries(entriesByEmployee).map(([empCode, empEntries]) => {
                    const company = companies.find(c => c.code === empCode);
                    const employee = employees.find(e => e.code === empCode);
                    return (
                      <div key={empCode} className="border-b border-slate-100 pb-4 last:border-0">
                        <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-slate-500" />
                          {employee?.name || empCode} - {company?.name || empCode}
                          <span className="text-sm font-normal text-slate-500">({empEntries.length} Einträge)</span>
                        </h3>
                        <div className="space-y-2">
                          {empEntries.slice(0, 10).map((entry) => (
                            <div key={entry.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 rounded-lg bg-slate-50 gap-2 text-sm">
                              <div className="flex items-center gap-3 flex-wrap">
                                <span className="text-slate-500">{format(new Date(entry.date), "dd.MM.yyyy", { locale: de })}</span>
                                <span className="font-mono text-slate-900">{entry.start_time} - {entry.end_time}</span>
                                <span className="text-blue-600 font-medium">{calculateWorkTime(entry.start_time, entry.end_time, entry.break_minutes)}</span>
                                {entry.einrichtung && (
                                  <span className="px-2 py-0.5 bg-purple-50 text-purple-600 rounded text-xs flex items-center gap-1">
                                    <Building2 className="w-3 h-3" />{entry.einrichtung}
                                  </span>
                                )}
                                {entry.station_wb && (
                                  <span className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded text-xs flex items-center gap-1">
                                    <MapPin className="w-3 h-3" />{entry.station_wb}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                {entry.signature ? (
                                  <>
                                    <span className="signed-badge text-xs"><Check className="w-3 h-3 inline mr-1" />{entry.signer_name || "Unterschrieben"}</span>
                                    <Button variant="outline" size="sm" onClick={() => generatePDF(entry, empCode)} className="text-blue-600 h-7 px-2 gap-1">
                                      <Download className="w-3 h-3" /><span className="text-xs">PDF</span>
                                    </Button>
                                  </>
                                ) : (
                                  <span className="unsigned-badge text-xs"><X className="w-3 h-3 inline mr-1" />Offen</span>
                                )}
                                <Button variant="ghost" size="sm" onClick={() => handleDeleteEntry(entry.id)} className="text-red-600 hover:bg-red-50 h-7 w-7 p-0">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Stammdaten Tab */}
        <TabsContent value="stammdaten" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="zeitflow-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Building2 className="w-5 h-5 text-purple-600" />
                  Neue Einrichtung
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreateEinrichtung} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Bezeichnung</Label>
                    <Input value={newEinrichtung.name} onChange={(e) => setNewEinrichtung({ ...newEinrichtung, name: e.target.value })} placeholder="z.B. Seniorenheim Musterstadt" />
                  </div>
                  <div className="space-y-2">
                    <Label>Farbe</Label>
                    <div className="flex flex-wrap gap-2">
                      {PROJECT_COLORS.map((color) => (
                        <button key={color} type="button" onClick={() => setNewEinrichtung({ ...newEinrichtung, color })}
                          className={`w-7 h-7 rounded-full transition-transform hover:scale-110 ${newEinrichtung.color === color ? "ring-2 ring-offset-2 ring-purple-600" : ""}`}
                          style={{ backgroundColor: color }} />
                      ))}
                    </div>
                  </div>
                  <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700" disabled={isLoading}>
                    <Plus className="w-4 h-4 mr-2" />Einrichtung erstellen
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card className="zeitflow-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <MapPin className="w-5 h-5 text-blue-600" />
                  Neue Station / WB
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreateStation} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Übergeordnete Einrichtung</Label>
                    <Select value={newStation.parent_id} onValueChange={(v) => setNewStation({ ...newStation, parent_id: v })}>
                      <SelectTrigger><SelectValue placeholder="Einrichtung auswählen..." /></SelectTrigger>
                      <SelectContent>
                        {globalEinrichtungen.map((e) => (
                          <SelectItem key={e.id} value={e.id}>
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: e.color }} />
                              {e.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Bezeichnung</Label>
                    <Input value={newStation.name} onChange={(e) => setNewStation({ ...newStation, name: e.target.value })} placeholder="z.B. Station A, WB Nord" />
                  </div>
                  <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={isLoading || !newStation.parent_id}>
                    <Plus className="w-4 h-4 mr-2" />Station erstellen
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          <Card className="zeitflow-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Globe className="w-5 h-5 text-purple-600" />
                Einrichtungen & Stationen
              </CardTitle>
            </CardHeader>
            <CardContent>
              {globalEinrichtungen.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Building2 className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>Noch keine Einrichtungen vorhanden</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {globalEinrichtungen.map((einrichtung) => {
                    const stationen = getStationenForEinrichtung(einrichtung.id);
                    const isExpanded = expandedEinrichtungen[einrichtung.id];
                    return (
                      <div key={einrichtung.id} className="border border-slate-200 rounded-lg overflow-hidden">
                        {editingProject?.id === einrichtung.id ? (
                          <div className="p-4 bg-purple-50 space-y-3">
                            <div className="flex items-center gap-3">
                              <Input 
                                value={editingProject.name} 
                                onChange={(e) => setEditingProject({...editingProject, name: e.target.value})}
                                className="flex-1"
                                placeholder="Name der Einrichtung"
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-slate-600">Farbe:</span>
                              {PROJECT_COLORS.map((color) => (
                                <button 
                                  key={color} 
                                  type="button" 
                                  onClick={() => setEditingProject({...editingProject, color})}
                                  className={`w-6 h-6 rounded-full transition-transform hover:scale-110 ${editingProject.color === color ? "ring-2 ring-offset-1 ring-purple-600" : ""}`}
                                  style={{ backgroundColor: color }} 
                                />
                              ))}
                            </div>
                            <div className="flex gap-2">
                              <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => { handleUpdateProject(editingProject.id, editingProject.name, editingProject.color); setEditingProject(null); }}>
                                <Check className="w-4 h-4 mr-1" /> Speichern
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setEditingProject(null)}>
                                <X className="w-4 h-4 mr-1" /> Abbrechen
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between p-4 bg-purple-50/50 cursor-pointer hover:bg-purple-50" onClick={() => toggleEinrichtung(einrichtung.id)}>
                            <div className="flex items-center gap-3">
                              {stationen.length > 0 ? (isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />) : <div className="w-4" />}
                              <div className="w-4 h-4 rounded-full" style={{ backgroundColor: einrichtung.color }} />
                              <Building2 className="w-4 h-4 text-purple-600" />
                              <span className="font-medium">{einrichtung.name}</span>
                              <span className="text-xs text-slate-500">({stationen.length} Station{stationen.length !== 1 ? 'en' : ''})</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setEditingProject({id: einrichtung.id, name: einrichtung.name, color: einrichtung.color}); }} className="text-slate-600 hover:bg-slate-100">
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDeleteProject(einrichtung.id); }} className="text-red-600 hover:bg-red-50">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        )}
                        {isExpanded && stationen.length > 0 && (
                          <div className="border-t border-slate-200 bg-white">
                            {stationen.map((station) => (
                              <div key={station.id} className="border-b border-slate-100 last:border-0">
                                {editingProject?.id === station.id ? (
                                  <div className="p-3 pl-12 bg-blue-50 space-y-3">
                                    <div className="flex items-center gap-3">
                                      <Input 
                                        value={editingProject.name} 
                                        onChange={(e) => setEditingProject({...editingProject, name: e.target.value})}
                                        className="flex-1"
                                        placeholder="Name der Station"
                                      />
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm text-slate-600">Farbe:</span>
                                      {PROJECT_COLORS.map((color) => (
                                        <button 
                                          key={color} 
                                          type="button" 
                                          onClick={() => setEditingProject({...editingProject, color})}
                                          className={`w-5 h-5 rounded-full transition-transform hover:scale-110 ${editingProject.color === color ? "ring-2 ring-offset-1 ring-blue-600" : ""}`}
                                          style={{ backgroundColor: color }} 
                                        />
                                      ))}
                                    </div>
                                    <div className="flex gap-2">
                                      <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => { handleUpdateProject(editingProject.id, editingProject.name, editingProject.color); setEditingProject(null); }}>
                                        <Check className="w-4 h-4 mr-1" /> Speichern
                                      </Button>
                                      <Button size="sm" variant="outline" onClick={() => setEditingProject(null)}>
                                        <X className="w-4 h-4 mr-1" /> Abbrechen
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex items-center justify-between p-3 pl-12 hover:bg-slate-50">
                                    <div className="flex items-center gap-3">
                                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: station.color }} />
                                      <MapPin className="w-4 h-4 text-blue-600" />
                                      <span>{station.name}</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <Button variant="ghost" size="sm" onClick={() => setEditingProject({id: station.id, name: station.name, color: station.color})} className="text-slate-600 hover:bg-slate-100 h-7 w-7 p-0">
                                        <Pencil className="w-3 h-3" />
                                      </Button>
                                      <Button variant="ghost" size="sm" onClick={() => handleDeleteProject(station.id)} className="text-red-600 hover:bg-red-50 h-7 w-7 p-0">
                                        <Trash2 className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminDashboard;
